import React, { useState, useRef } from 'react';
import styled from '@emotion/styled';
import OrganizationContainer from '../../state/containers/OrganizationContainer';
import MediaContainer from '../../state/containers/MediaContainer';
import UserAvatar from './UserAvatar';
import SecondaryLoader from './SecondaryLoader';
import { color_shades_dark, color_defaults_white, voicify_spark_light, voicify_spark_lightest } from '../../constants/colors';

export interface OrganizationAvatarUploadProps {
    organizationId: string;
    organizationName: string;
    currentImageUri?: string | null;
    orgContainer: OrganizationContainer;
    mediaContainer: MediaContainer;
    size?: 'small' | 'large';
    showLabel?: boolean;
    showHint?: boolean;
    isLoading?: boolean;
    disabled?: boolean;
    onError?: (errors: string[]) => void;
    onClearError?: () => void;
    onUploadSuccess?: (imageUrl: string) => void;
}

const OrganizationAvatarUpload: React.FC<OrganizationAvatarUploadProps> = ({
    organizationId,
    organizationName,
    currentImageUri,
    orgContainer,
    mediaContainer,
    size = 'small',
    showLabel = false,
    showHint = false,
    isLoading = false,
    disabled = false,
    onError,
    onClearError,
    onUploadSuccess
}) => {
    const [customAvatarUrl, setCustomAvatarUrl] = useState<string | null>(null);
    const [isUploadingAvatar, setIsUploadingAvatar] = useState<boolean>(false);
    const avatarInputRef = useRef<HTMLInputElement>(null);

    const avatarSize = size === 'large' ? 175 : 120;
    const wrapperSize = size === 'large' ? 215 : 140;
    const ringSize = size === 'large' ? 195 : 135;
    const innerRingSize = size === 'large' ? 183 : 126;
    const overlayIconSize = size === 'large' ? 32 : 24;
    const overlayTextSize = size === 'large' ? 12 : 11;

    const handleAvatarClick = () => {
        if (disabled) return;
        avatarInputRef.current?.click();
    };

    const handleAvatarFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                onError?.(['Invalid file type. Please select a JPG, PNG, GIF, or WebP image.']);
                return;
            }

            // Validate file size (max 5MB)
            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
                onError?.([`Image is too large (${fileSizeMB}MB). Please select an image under 5MB.`]);
                return;
            }

            // Clear any previous errors
            onClearError?.();
            setIsUploadingAvatar(true);

            try {
                // Upload the image to get a URL
                const uploadResult = await mediaContainer.uploadOrganizationMedia(
                    organizationId,
                    file.name,
                    file,
                    '',
                    () => { }
                );

                if (uploadResult.resultType === 'Ok' && uploadResult.data) {
                    const imageUrl = uploadResult.data;

                    // Update the organization with the new image URL
                    await orgContainer.updateOrganizationInfo({
                        name: organizationName,
                        description: orgContainer.state.currentOrganization?.description,
                        imageUri: imageUrl
                    });

                    // Check if there were errors from the organization update
                    if (orgContainer.state.errors && orgContainer.state.errors.length > 0) {
                        onError?.(orgContainer.state.errors);
                    } else {
                        // Update local state with the new image URL
                        setCustomAvatarUrl(imageUrl);
                        onUploadSuccess?.(imageUrl);
                    }
                } else {
                    onError?.(['Failed to upload image. Please try a smaller file or different format (JPG, PNG recommended).']);
                }
            } catch (error: any) {
                // uploadOrganizationMedia rejects with string[] on API errors, or an Error/string on network errors
                if (Array.isArray(error) && error.length > 0) {
                    onError?.(error.map(e => typeof e === 'string' ? e : String(e)));
                } else {
                    const message = error?.message || (typeof error === 'string' ? error : null);
                    onError?.([`Failed to upload image. ${message || 'Please try a smaller file size (under 5MB) or a different image format (JPG, PNG recommended).'}`]);
                }
            } finally {
                setIsUploadingAvatar(false);
                // Reset the input so the same file can be selected again if needed
                if (avatarInputRef.current) {
                    avatarInputRef.current.value = '';
                }
            }
        }
    };

    const getCurrentAvatarUrl = () => {
        if (customAvatarUrl) return customAvatarUrl;
        return currentImageUri || null;
    };

    const getOrgInitials = () => {
        if (!organizationName) return 'ORG';
        return organizationName
            .split(' ')
            .map(word => word.charAt(0))
            .join('')
            .toUpperCase()
            .substring(0, 3);
    };

    const showLoading = isLoading || isUploadingAvatar;

    return (
        <AvatarSection>
            {showLabel && <AvatarLabel>Organization Logo</AvatarLabel>}
            <input
                type="file"
                ref={avatarInputRef}
                onChange={handleAvatarFileChange}
                accept="image/*"
                style={{ display: 'none' }}
                disabled={disabled}
            />
            {showLoading ? (
                <AvatarLoaderWrapper
                    onClick={handleAvatarClick}
                    wrapperSize={wrapperSize}
                >
                    <AvatarRing ringSize={ringSize} />
                    <AvatarInnerRing innerRingSize={innerRingSize} />
                    <AvatarLoaderContent avatarSize={avatarSize}>
                        <SecondaryLoader className="grey" />
                    </AvatarLoaderContent>
                </AvatarLoaderWrapper>
            ) : (
                <ClickableAvatarWrapper
                    onClick={handleAvatarClick}
                    title={disabled ? undefined : 'Click to change avatar'}
                    wrapperSize={wrapperSize}
                    avatarSize={avatarSize}
                    disabled={disabled}
                >
                    <AvatarRing ringSize={ringSize} />
                    <AvatarInnerRing innerRingSize={innerRingSize} />
                    <UserAvatar
                        firstName={organizationName || 'O'}
                        lastName=""
                        src={getCurrentAvatarUrl()}
                        size={avatarSize}
                        noShadow={true}
                        noImageText={getOrgInitials()}
                    />
                    {!disabled && (
                        <AvatarUploadOverlay avatarSize={avatarSize}>
                            <AvatarUploadIcon viewBox="0 0 24 24" iconSize={overlayIconSize}>
                                <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
                            </AvatarUploadIcon>
                            <AvatarUploadText fontSize={overlayTextSize}>Change</AvatarUploadText>
                        </AvatarUploadOverlay>
                    )}
                </ClickableAvatarWrapper>
            )}
            {showHint && <AvatarHint>Click to upload a new avatar (.png | .jpeg | .jpg | max 5MB)</AvatarHint>}
        </AvatarSection>
    );
};

const AvatarSection = styled.div``;

const AvatarLabel = styled.label`
    display: block;
    font-family: Muli;
    font-size: 14px;
    text-align: left;
    margin: 0 16px 12px 4px;
    font-weight: 600;
`;

const AvatarHint = styled.p`
    font-size: 12px;
    color: ${color_shades_dark};
    margin-top: 8px;
    margin-bottom: 0;
`;

const AvatarLoaderWrapper = styled.div<{ wrapperSize: number }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${props => props.wrapperSize}px;
    height: ${props => props.wrapperSize}px;
    cursor: pointer;
`;

const AvatarLoaderContent = styled.div<{ avatarSize: number }>`
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${props => props.avatarSize}px;
    height: ${props => props.avatarSize}px;
    border-radius: 50%;
    background: ${color_defaults_white};
    z-index: 2;
`;

const ClickableAvatarWrapper = styled.div<{ wrapperSize: number; avatarSize: number; disabled?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${props => props.wrapperSize}px;
    height: ${props => props.wrapperSize}px;
    cursor: ${props => props.disabled ? 'default' : 'pointer'};
    opacity: ${props => props.disabled ? 0.5 : 1};
    
    /* White background circle to ensure logos with transparent backgrounds look good */
    &::before {
        content: '';
        position: absolute;
        width: ${props => props.avatarSize}px;
        height: ${props => props.avatarSize}px;
        border-radius: 50%;
        background: ${color_defaults_white};
        z-index: 1;
    }
    
    /* Ensure the avatar is above the background */
    > *:not(:first-child):not(:nth-child(2)) {
        z-index: 2;
    }

    &:hover {
        > div:last-of-type {
            opacity: 1;
        }
    }
`;

const AvatarRing = styled.div<{ ringSize: number }>`
    position: absolute;
    width: ${props => props.ringSize}px;
    height: ${props => props.ringSize}px;
    border-radius: 50%;
    background: ${voicify_spark_lightest};
    opacity: 0.6;
`;

const AvatarInnerRing = styled.div<{ innerRingSize: number }>`
    position: absolute;
    width: ${props => props.innerRingSize}px;
    height: ${props => props.innerRingSize}px;
    border-radius: 50%;
    border: 2px solid ${voicify_spark_light};
    background: transparent;
`;

const AvatarUploadOverlay = styled.div<{ avatarSize: number }>`
    position: absolute;
    width: ${props => props.avatarSize}px;
    height: ${props => props.avatarSize}px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: 10;
    pointer-events: none;
`;

const AvatarUploadIcon = styled.svg<{ iconSize: number }>`
    width: ${props => props.iconSize}px;
    height: ${props => props.iconSize}px;
    fill: ${color_defaults_white};
    margin-bottom: 4px;
`;

const AvatarUploadText = styled.span<{ fontSize: number }>`
    color: ${color_defaults_white};
    font-size: ${props => props.fontSize}px;
    font-weight: 600;
`;

export default OrganizationAvatarUpload;
